import { configRepo } from '@/repository/config'
import Facade from '@/Facade'
import { dialogRepo } from '@/repository/dialog'
import { gameRepo } from '@/repository/game'
import { navRepo } from '@/repository/nav'
import { preloadRepo } from '@/repository/preload'
import { translationRepo } from '@/repository/translation'
import { getPaytableConfig } from '@/ui-content/paytable'
import { getRulesConfig } from '@/ui-content/rules'
import brandLogoAtlas from '@/assets/brandlogo0.json'
import { TournamentManager } from '@/managers/TournamentManager'
import brandLogoImage from '@/assets/brandlogo0.png'
import GameSignals from '@/signals/GameSignals'
import { ExternalUIBridgeManager } from '@modules/src/managers/ExternalUIBridgeManager'
import {
    ExternalUIAction,
    IExternalUISnapshot,
    TTournamentBadgeConfig,
} from '@modules/src/interfaces/IExternalUIBridge'

export class ExternalUiAdapter {
    private _unsubscribeAction: (() => void) | null = null
    private _unsubscribers: Array<() => void> = []
    private _tournamentBadgeConfigFromPublic: TTournamentBadgeConfig | null = null
    private _tournamentBadgeConfigLoadAttempted = false

    private normalizeVolume = (raw: unknown): number => {
        const value = Number(raw)
        if (!Number.isFinite(value)) return Number.NaN

        // Accept both 0..1 and 0..100 inputs; keep snapshot in 0..1 range.
        const normalized = value > 1 ? value / 100 : value
        return Math.max(0, Math.min(1, normalized))
    }

    private normalizeAutoplayCount = (raw: unknown): number => {
        const value = Number(raw)
        if (!Number.isFinite(value) || value <= 0) {
            return Number.MAX_SAFE_INTEGER
        }

        return Math.floor(value)
    }

    start() {
        ExternalUIBridgeManager.mount(window)

        if (Facade.model) {
            ;(Facade.model as any).preferredAutoplayCount = Number.MAX_SAFE_INTEGER
        }

        void this.loadTournamentBadgeConfigFromPublic()

        this.pushSnapshot()

        this._unsubscribeAction = ExternalUIBridgeManager.onAction(this.onAction)

        this._unsubscribers.push(
            gameRepo.subscribe(() => this.pushSnapshot()),
            translationRepo.subscribe(() => this.pushSnapshot()),
            configRepo.subscribe(() => this.pushSnapshot()),
            dialogRepo.subscribe(() => this.pushSnapshot()),
            navRepo.subscribe(() => this.pushSnapshot()),
            preloadRepo.subscribe(() => this.pushSnapshot()),
            GameSignals.backendMessage.connect(() => this.pushSnapshot())
        )
    }

    stop() {
        this._unsubscribeAction?.()
        this._unsubscribeAction = null

        this._unsubscribers.forEach((unsubscribe) => unsubscribe())
        this._unsubscribers = []

        ExternalUIBridgeManager.unmount()
    }

    private async loadTournamentBadgeConfigFromPublic() {
        if (this._tournamentBadgeConfigLoadAttempted) {
            return
        }

        this._tournamentBadgeConfigLoadAttempted = true

        try {
            const response = await fetch('/assets/json/TournamentBadgeConfig.json', {
                cache: 'no-store',
            })

            if (!response.ok) {
                console.warn('[ExternalUiAdapter] TournamentBadgeConfig.json not loaded', {
                    status: response.status,
                })
                return
            }

            const config = (await response.json()) as TTournamentBadgeConfig
            this._tournamentBadgeConfigFromPublic = config

            console.log('[ExternalUiAdapter] Tournament badge config loaded from public JSON')

            this.pushSnapshot()
        } catch (error) {
            console.warn('[ExternalUiAdapter] Failed to load TournamentBadgeConfig.json', error)
        }
    }

    private pushSnapshot() {
        const gameState = gameRepo.getState()
        const configState = configRepo.getState()
        const navState = navRepo.getState()
        const preloadState = preloadRepo.getState()
        const translate = translationRepo.getState().translate
        const spinData = Facade.model?.spinData || gameState.spinData
        const freeRound = spinData?.freeround
        const freeRoundsCurrent = Number(freeRound?.amount_left)
        const freeRoundsTotal = Number(freeRound?.total_amount_given)
        const freeRoundsActive =
            Boolean(freeRound?.is_active) && Number.isFinite(freeRoundsTotal) && freeRoundsTotal > 0
        const promoType = String(spinData?.promo_details?.type || '').toLowerCase()
        const promoFreeGamesCurrent = Number(spinData?.freebet?.amount_left ?? spinData?.promo_details?.spins_left)
        const promoFreeGamesTotal = Number(spinData?.freebet?.total_amount_given ?? spinData?.promo_details?.num_spins)
        const promoFreeGamesActive =
            (promoType === 'promospins' || promoType === 'promospin') &&
            Number.isFinite(promoFreeGamesCurrent) &&
            promoFreeGamesCurrent > 0 &&
            Number.isFinite(promoFreeGamesTotal) &&
            promoFreeGamesTotal > 0

        if (promoFreeGamesActive) {
            console.log('[ExternalUiAdapter] promo badge snapshot', {
                promoType,
                current: Number.isFinite(promoFreeGamesCurrent) ? Math.max(0, promoFreeGamesCurrent) : 0,
                total: Number.isFinite(promoFreeGamesTotal) ? Math.max(0, promoFreeGamesTotal) : 0,
                hasFreebet: !!spinData?.freebet,
                spinsLeft: spinData?.promo_details?.spins_left,
            })
        }

        // Tournament data from Model
        const tournamentData = Facade.model?.tournamentData || Facade.model?.initData?.tournament_data
        const tournamentBadgeConfig =
            this._tournamentBadgeConfigFromPublic ||
            (Facade as any).model?.tournamentBadgeConfig ||
            (Facade as any).model?.initData?.tournament_badge_config ||
            tournamentData?.badge_config ||
            tournamentData?.trophy_config
        const showTournament = (Facade as any)._showTournament || false
        const showTournamentBadge = (Facade as any)._showTournamentBadge || false

        if (tournamentData) {
            console.log('[ExternalUiAdapter] Tournament snapshot:', {
                hasTournamentData: !!tournamentData,
                tournamentId: tournamentData?.tournament_id,
                showTournament,
            })
        }

        const facadeVolume = this.normalizeVolume(Facade.defaultConfig?.volume)
        const stateVolume = this.normalizeVolume((gameState as unknown as { volume?: number }).volume)
        const resolvedVolume = Number.isFinite(facadeVolume)
            ? facadeVolume
            : Number.isFinite(stateVolume)
              ? stateVolume
              : gameState.sound || gameState.music
                ? 1
                : 0

        const snapshot: Partial<IExternalUISnapshot> = {
            preloadProgress: preloadState.isActive ? Number(preloadState.progress || 0) : 100,
            preloadActive: !!preloadState.isActive,
            preloadLogoUrl: brandLogoImage,
            preloadLogoAtlas: brandLogoAtlas,
            infoPages: {
                paytable: getPaytableConfig(translate),
                rules: getRulesConfig(translate),
            },
            uiText: {
                settings: translate('SETTINGS') || 'Settings',
                paytable: translate('PAYTABLE') || 'Paytable',
                rules: translate('RULES') || 'Rules',
                volume: translate('Volume') || 'Volume',
                freeRoundsLabel: translate('FREE SPINS') || 'Free Spins',
                options: translate('OPTIONS') || 'Options',
                soundEffects: translate('Sound Effects') || 'Sound Effects',
                ambienceSound: translate('Ambience Sound') || 'Ambience Sound',
                turboSpin: translate('Quick Spin') || 'Quick Spin',
                useSpacebarToSpin: translate('Press space bar to spin/stop') || 'Press space bar to spin/stop',
                autoSpin: translate('AUTO SPIN') || 'Auto Spin',
                autoPlay: translate('Auto Play') || 'Auto Play',
                startAutoSpins: translate('START AUTO SPINS') || 'Start Auto Spins',
                stopOnAnyWin: translate('Stop on Any Win') || 'Stop on Any Win',
                stopOnSingleWinLimit: translate('Stop on Single Win Limit') || 'Stop on Single Win Limit',
                stopOnSessionLossLimit: translate('Stop on Session Loss Limit') || 'Stop on Session Loss Limit',
            },
            freeRoundsCounter: freeRoundsActive
                ? {
                      current: Number.isFinite(freeRoundsCurrent) ? Math.max(0, freeRoundsCurrent) : 0,
                      total: Number.isFinite(freeRoundsTotal) ? Math.max(0, freeRoundsTotal) : 0,
                  }
                : undefined,
            promoFreeGamesCounter: promoFreeGamesActive
                ? {
                      current: Number.isFinite(promoFreeGamesCurrent) ? Math.max(0, promoFreeGamesCurrent) : 0,
                      total: Number.isFinite(promoFreeGamesTotal) ? Math.max(0, promoFreeGamesTotal) : 0,
                  }
                : undefined,
            balance: gameState.balance,
            currencySymbol: gameState.currency_symbol,
            currentBet: gameState.current_bet,
            state: gameState.state,
            stopSpin: !!gameState.stop_spin,
            isUIActive: configState.isUIActive,
            showUI: configState.showUI,
            menu: navState.active?.menu || null,
            language: gameState.language,
            volume: resolvedVolume,
            sound: !!gameState.sound,
            music: !!gameState.music,
            isMobile: !!configState.isMobile,
            turboSpin: !!gameState.turbo_spin,
            spacebarSpin: !!gameState.spacebar_spin,
            dialogs: this.getDialogsSnapshot(),
            tournamentData: tournamentData || undefined,
            tournamentBadgeConfig: tournamentBadgeConfig || undefined,
            showTournament: showTournament || false,
            showTournamentBadge: showTournamentBadge || false,
        }

        ExternalUIBridgeManager.pushSnapshot(snapshot)
    }

    private getDialogsSnapshot(): IExternalUISnapshot['dialogs'] {
        return dialogRepo.getState().items.map((dialog) => {
            const close = () => dialogRepo.getState().close(dialog.id)

            return {
                id: dialog.id,
                variant: dialog.variant,
                key: dialog.key,
                title: dialog.title,
                text: dialog.text,
                style: dialog.style,
                html: dialog.html,
                isCloseable: dialog.isCloseable,
                overlay: dialog.overlay,
                onClose: dialog.isCloseable ? close : undefined,
                buttons: dialog.buttons?.map((button) => ({
                    label: button.label,
                    onClick: () => {
                        if (button.callback) {
                            button.callback(close)
                            return
                        }

                        close()
                    },
                })),
            }
        })
    }

    private onAction = (action: ExternalUIAction) => {
        const send = gameRepo.getState().send

        switch (action.type) {
            case 'open_settings':
                navRepo.getState().openNav('settings')
                configRepo.getState().changeUIActive(true)
                break
            case 'close_settings':
                configRepo.getState().changeUIActive(false)
                break
            case 'spin':
                send('clicked_play')
                break
            case 'stop':
                send('clicked_stop')
                break
            case 'set_bet':
                send('set_bet', { value: action.value })
                break
            case 'set_volume':
                {
                    const volume = this.normalizeVolume(action.value)
                    const isAudioEnabled = volume > 0

                    // Keep local UI state coherent even if backend does not echo intermediate volume updates.
                    if (Facade.defaultConfig) {
                        Facade.defaultConfig.volume = volume
                        Facade.defaultConfig.sound = isAudioEnabled
                        Facade.defaultConfig.music = isAudioEnabled
                    }

                    send('set_volume', { value: volume })
                    send('set_sound', { value: isAudioEnabled })
                    send('set_music', { value: isAudioEnabled })

                    this.pushSnapshot()
                }
                break
            case 'set_sound':
                send('set_sound', { value: action.value })
                break
            case 'set_music':
                send('set_music', { value: action.value })
                break
            case 'set_turbo_spin':
                send('set_turbo_spin', { value: action.value })
                break
            case 'set_spacebar_spin':
                send('set_spacebar_spin', { value: action.value })
                break
            case 'set_autoplay_count':
                if (Facade.model) {
                    ;(Facade.model as any).preferredAutoplayCount = this.normalizeAutoplayCount(action.value)
                }
                break
            case 'start_autospin':
                if (Facade.model) {
                    ;(Facade.model as any).preferredAutoplayCount = this.normalizeAutoplayCount(
                        action.value.autoplayCount
                    )
                }
                send('set_autoplay_stop_cash_increase_enabled', { value: action.value.stopOnWinLimit })
                send('set_autoplay_stop_cash_decrease_enabled', { value: action.value.stopOnLossLimit })
                send('set_autoplay_stop_any_win', { value: action.value.stopOnAnyWin })
                send('set_stop_single_win', { value: action.value.stopSingleWinValue })
                send('set_stop_balance_lower', { value: action.value.stopLossValue })
                send('set_autoplay_stop_cash_decrease', { value: action.value.stopLossValue })
                send('set_autoplay', { value: action.value.autoplayCount })
                configRepo.getState().changeUIActive(false)
                break
            case 'open_tournament':
                // Request fresh leaderboard data and open popup
                ;(GameSignals as any).openTournament?.emit()
                break
            case 'open_tournament_popup':
                TournamentManager.instance.openFromBadge()
                break
            case 'close_tournament':
                TournamentManager.instance.close()
                break
            case 'join_tournament':
                // Emit signal to join tournament
                ;(GameSignals as any).joinTournament?.emit(action.tournamentId)
                break
            default:
                break
        }
    }
}

export default ExternalUiAdapter
